import _debounce from 'lodash.debounce'
import * as types from './types'
import { apiUsers } from '../../api'

import TimeAndDate from 'utils/TimeAndDate'

const searchDebounce = _debounce((dispatch) => { dispatch(searchUsers()) }, 250)

export const startFetchUsersQueryRequest = () => ({ type: types.FETCH_USERS_QUERY_REQUEST })
export const fetchUsersQuerySuccess = (users, total_users) => ({
  type: types.FETCH_USERS_QUERY_SUCCESS,
  users,
  total_users,
})
export const fetchUsersQueryFailure = (error) => ({ type: types.FETCH_USERS_QUERY_FAILURE, error })
export const clearSearchResults = () => ({ type: types.CLEAR_SEARCH_RESULTS })
export const startSearchUsersRequest = () => ({ type: types.SEARCH_USERS_REQUEST })
export const searchUsersSuccess = (users, count) => ({
  type: types.SEARCH_USERS_SUCCESS,
  users,
  count,
})
export const searchUsersFailure = (error) => ({ type: types.SEARCH_USERS_FAILURE, error })

export const resetUsersSearchFilters = () => (dispatch) => {
  dispatch({ type: types.RESET_USER_SEARCH_FILTERS })
  dispatch(searchUsers())
}

export const updateSearchtext = (field, value) => (dispatch) => {
  dispatch({ field, value, type: types.UPDATE_USERS_SEARCH_FILTERS })

  searchDebounce(dispatch)
}

/**
 * Update result order
 * @param {object} params
 * @param {string} params.sortField
 */
export const updateUserSearchOrder = (params) => (dispatch) => {
  dispatch({ type: types.UPDATE_USERS_SEARCH_ORDER, payload: params })

  searchDebounce(dispatch)
}

export const updateUsersSearchFilters = (field, value) => (dispatch) => {
  dispatch({ type: types.UPDATE_USERS_SEARCH_FILTERS, field, value })

  return dispatch(searchUsers())
}
export const setUsersSearchChecks = (conversationsId) => ({
  type: types.SET_USERS_SEARCH_CHECKS,
  conversationsId,
})
export const toggleSearchUsersIsAllChecks = () => ({
  type: types.TOGGLE_USERS_SEARCH_IS_ALL_CHECKS
})

export const setColumnsView = cols_order => ({
  type: types.SET_USERS_COLUMNS_VIEW,
  cols_order
})

export const setCenters = (brands, delegations) => {
  return {
    type: types.SEARCH_USERS_SET_CENTERS,
    brands, delegations
  }
}

export const updateUser = (user) => {
  return {
    type: types.SEARCH_USERS_UPDATE_USER,
    user
  }
}

export const fetchUsersQuery = () => (dispatch, getState) => {
  dispatch(startSearchUsersRequest())

  const {
    cv_search: { sortField, sortType, page },
    user: { company },
    brands,
    delegations,
  } = getState()

  let query = `?sort=${sortField}&order=${sortType}&from=${page}`

  if (Object.keys(delegations).length > 0) {
    query += `&delegations=${Object.values(delegations)
      .map((e) => e._id)
      .join()}`
  }
  if (company) {
    query += `&companies=${company}`
  }
  if (Object.keys(brands).length > 0) {
    query += `&brands=${Object.values(brands)
      .map((e) => e._id)
      .join()}`
  }

  apiUsers
    .byQuery(query)
    .then(({ data, status }) => {
      if (status === 200) dispatch(searchUsersSuccess(data.users, data.countUsers))
      else if (status === 204) dispatch(searchUsersSuccess([], 0))
      else dispatch(searchUsersFailure({ status: data.statusOk, message: data.error }))
    })
    .catch((error) => dispatch(searchUsersFailure(error)))
}

export const getQueryToSearch = (cvSearch, newFilters = {}) => {
  const searchUsers = { ...cvSearch, ...newFilters }

  const { filters } = searchUsers

  let query = {
    limit: filters.limit,
    sort: filters.sortField,
    order: filters.sortType === 'desc' ? 1 : -1,
    page: filters.page
  }
  const queryFilters = {}
  Object.keys(filters).forEach((f) => {
    const aFilters = filters[f]

    if (aFilters && Array.isArray(aFilters)) {
      queryFilters[f] = aFilters.length > 0
        ? aFilters.map((elem) => elem.value).join()
        : aFilters
    }
  })

  if (filters.searchText) {
    query.text = filters.searchText
  }
  if (filters.outbound) {
    query.outbound = filters.outbound
  }
  if (queryFilters.assigned) {
    query.assigned = queryFilters.assigned
  }
  if (queryFilters.providers) {
    query.providers = queryFilters.providers
  }
  if (queryFilters.brands) {
    query.brands = queryFilters.brands
  }
  if (queryFilters.delegations) {
    query.delegations = queryFilters.delegations
  }
  if (queryFilters.states) {
    query.states = queryFilters.states
  }
  if (queryFilters.labels) {
    query.categories = queryFilters.labels
  }
  if (queryFilters.folders) {
    query.lists = queryFilters.folders
  }
  if (queryFilters.companies) {
    query.companies = queryFilters.companies
  }
  if (queryFilters.languages) {
    query.languages = queryFilters.languages
  }
  if (filters.createdAt?.[0] || filters.createdAt?.[1]) {
    query.createdAt = TimeAndDate.betweenEndStartDayUTC(
      filters.createdAt?.[0],
      filters.createdAt?.[1]
    )
  }
  if (filters.updatedAt?.[0] || filters.updatedAt?.[1]) {
    query.updatedAt = TimeAndDate.betweenEndStartDayUTC(
      filters.updatedAt?.[0],
      filters.updatedAt?.[1]
    )
  }
  if (filters.lastMessageDate?.[0] || filters.lastMessageDate?.[1]) {
    query.lastMessageDate = TimeAndDate.betweenEndStartDayUTC(
      filters.lastMessageDate?.[0],
      filters.lastMessageDate?.[1]
    )
  }

  return query
}

export const searchUsers = (newFilters = {}) => (dispatch, getState) => {
  dispatch(startSearchUsersRequest())
  const { cv_search } = getState()

  const query = getQueryToSearch(cv_search, newFilters)

  return apiUsers
    .bySearch(query)
    .then(({ data, status }) => {
      if (status === 200) {
        dispatch(searchUsersSuccess(data.users, data.countUsers))
        return data.filters
      } else if (status === 204) {
        dispatch(searchUsersSuccess([], 0))
        return {}
      }
    })
    .catch((error) => dispatch(searchUsersFailure(error)))
}

export const getFilters = () => dispatch => {
  return apiUsers.getFilters()
    .then(({ data, status }) => {
      if (status === 200) {
        dispatch(setCenters(data.brands, data.delegations))
        return data
      } else {
        return {}
      }
    })
    .catch((error) => {
      console.error(error)
      return {}
    })
}
